import axios from 'axios';
import baseURL from '../Config';

const RETURN_KEY = 'syncro.impersonation.return';
const read = key => {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
};
const tokenOf = payload => payload?.data?.token || payload?.data?.accessToken;
export const currentSession = () => read('userDetails') || read('adminUser');
export const impersonationActive = () => Boolean(currentSession()?.data?.impersonation || read(RETURN_KEY));
const headers = payload => ({ authorization: `Bearer ${tokenOf(payload)}` });
const reloadAt = path => {
    // Update the destination without dispatching hashchange into the old React
    // tree, which still contains the previous user's Redux state and effects.
    window.history.replaceState(window.history.state, '', `#${path}`);
    window.location.reload();
};
const persist = payload => {
    localStorage.setItem('userDetails', JSON.stringify(payload));
    localStorage.setItem('adminUser', JSON.stringify(payload));
};

export async function listImpersonationUsers() {
    const response = await axios.get(`${baseURL}impersonation/users`, { headers: headers(currentSession()) });
    return response.data.data.items;
}

export async function startImpersonation(userId) {
    if (impersonationActive()) throw new Error('Vuelve a tu cuenta antes de elegir otro usuario.');
    const original = currentSession();
    const response = await axios.post(`${baseURL}impersonation/start`, { userId }, { headers: headers(original) });
    const payload = response.data;
    localStorage.setItem(RETURN_KEY, JSON.stringify({ original, sessionId: payload.data.impersonation.sessionId }));
    persist(payload);
    reloadAt('/');
}

export async function stopImpersonation({ logout = false } = {}) {
    const saved = read(RETURN_KEY);
    if (!saved?.original) {
        localStorage.removeItem('userDetails');
        localStorage.removeItem('adminUser');
        localStorage.removeItem(RETURN_KEY);
        reloadAt('/login');
        return;
    }
    try {
        const response = await axios.post(`${baseURL}impersonation/stop`, { sessionId: saved.sessionId }, { headers: headers(saved.original) });
        if (logout) {
            localStorage.removeItem('userDetails');
            localStorage.removeItem('adminUser');
        } else {
            persist({ ...saved.original, data: { ...saved.original.data, adminUser: response.data.data.adminUser } });
        }
        localStorage.removeItem(RETURN_KEY);
        reloadAt(logout ? '/login' : '/user-list');
    } catch (error) {
        if ([401, 403].includes(error.response?.status)) {
            localStorage.removeItem(RETURN_KEY);
            localStorage.removeItem('userDetails');
            localStorage.removeItem('adminUser');
            reloadAt('/login');
            return;
        }
        throw error;
    }
}

// All existing consumers use these two storage keys. Reload other tabs before
// they can combine the new token with a screen belonging to the previous user.
export function installSessionSync() {
    const initialToken = tokenOf(currentSession());
    const initiallyImpersonating = impersonationActive();
    const changed = () => tokenOf(currentSession()) !== initialToken;
    window.addEventListener('storage', event => {
        if ((!event.key || ['userDetails', 'adminUser'].includes(event.key)) && changed()) window.location.reload();
    });
    axios.interceptors.request.use(config => {
        if (changed() && (initiallyImpersonating || impersonationActive())) {
            window.location.reload();
            return Promise.reject(new axios.Cancel('La sesión cambió.'));
        }
        config.syncroSessionToken = tokenOf(currentSession());
        return config;
    });
    const belongsToPreviousSession = config => config &&
        Object.prototype.hasOwnProperty.call(config, 'syncroSessionToken') &&
        config.syncroSessionToken !== tokenOf(currentSession());
    axios.interceptors.response.use(response => {
        if (belongsToPreviousSession(response.config)) throw new axios.Cancel('La sesión cambió.');
        return response;
    }, error => {
        // An old session's response is no longer relevant. Errors from the
        // current session (including 401/403/500 and network errors) still surface.
        if (belongsToPreviousSession(error.config)) return Promise.reject(new axios.Cancel('La sesión cambió.'));
        return Promise.reject(error);
    });
}
